(function () {
    'use strict';

    angular
        .module('app')
        .factory('ItemService', ItemService);

    ItemService.$inject = ['$http', '$q', 'UrlService', 'DataService'];

    /**
     * @ngdoc service
     * @name ItemService
     *
     * @description
     * Data service for 'items'.
     * 
     * An 'item' is a catch-all for a variety of object types, like entities, tasks, notes.
     * The app may display an "item list", which will show only common properties from a
     * list of disparate item types.
     */
    function ItemService($http, $q, UrlService, DataService) {
        var dataSrv = DataService('item');

        var itemCopyStatuses = {
            ToBeCopied: 'ToBeCopied',
            ItemTypeCannotBeCopied: 'ItemTypeCannotBeCopied',
            Copied: 'Copied',
            Error: 'Error'
        };

        /**
         * Metadata for item types.
         * Use 'predicate' with _.isMatch function to check whether an item matches a given type, or,
         * use ItemService.getItemTypeMetaData function.
         */
        var itemTypeMetaData = [
            {
                predicate: { Type: 'DocumentTemplate' },
                singular: 'Document Template',
                plural: 'Document Templates',
                canCopy: true,
                canMove: false
            },
            {
                predicate: { Type: 'Email', EmailStatus: 'Draft' },
                singular: 'Draft Email',
                plural: 'Draft Emails',
                canCopy: true,
                canMove: false
            },
            {
                predicate: { Type: 'Email', EmailStatus: 'Sent' },
                singular: 'Sent Email',
                plural: 'Sent Emails',
                canCopy: false,
                canMove: true
            },
            {
                predicate: { Type: 'Email', EmailStatus: 'Received' },
                singular: 'Received Email',
                plural: 'Received Emails',
                canCopy: false,
                canMove: true
            },
            {
                predicate: { Type: 'Entity' },
                singular: 'Entity',
                plural: 'Entities',
                canCopy: true,
                canMove: false
            },
            {
                predicate: { Type: 'File' },
                singular: 'File',
                plural: 'Files',
                canCopy: true,
                canMove: true
            },
            {
                predicate: { Type: 'Note' },
                singular: 'Note',
                plural: 'Notes',
                canCopy: true,
                canMove: true
            },
            {
                predicate: { Type: 'SMS' },
                singular: 'Sent SMS',
                plural: 'Sent SMS',
                canCopy: false,
                canMove: true
            },
            {
                predicate: { Type: 'Task' },
                singular: 'Task',
                plural: 'Tasks',
                canCopy: true,
                canMove: true
            }
        ];

        var service = {
            getItemsByParent: getItemsByParent,
            get: get,
            permanentlyDeleteItems: permanentlyDeleteItems,
            deleteItems: deleteItems,
            copyItems: copyItems,
            itemTypeMetaData: itemTypeMetaData,
            summariseItemList: summariseItemList,
            itemCopyStatuses: itemCopyStatuses,
            getItemTypeMetaData: getItemTypeMetaData,
            getItemLink: getItemLink,
            bulkEdit: bulkEdit,
            restoreItem: restoreItem,
            restoreItems: restoreItems,
            count: count,
            getUsage: getUsage
        };

        return service;

        /**
         * Get a list of items parented by the specified item.
         *
         * @param {number} parentId
         * @param {object} searchOptions - Optional. An object with the following properties:
         *   {string} recordType - Optional. The record type to limit the results to, e.g. "Note", "SMS", etc.
         *   {string} description - Optional. Search text for the item description.
         *   {number} userId - Optional. User id to filter items, returns items last updated by this user.
         *   {array} tagIds - Optional. Array of {number}, each representing a tag id.
         *   {number} pageNumber - Optional.
         *   {number} pageSize - Optional.
         *   {array} excludeLinkTypes - Optional. Array of {string}, corresponding to a link type that should be excluded (e.g. 'Entity Image')
         */
        function getItemsByParent(parentId, searchOptions) {
            var queryParams = {
                ParentId: parentId
            };

            if (!_.isNil(searchOptions)) {
                if (!_.isNil(searchOptions.recordType)) {
                    queryParams.RecordType = searchOptions.recordType;
                }

                if (!_.isNil(searchOptions.description)) {
                    queryParams.Description = searchOptions.description;
                }

                if (!_.isNil(searchOptions.userId)) {
                    queryParams.UserId = searchOptions.userId;
                }

                if (!_.isNil(searchOptions.tagIds)) {
                    queryParams.TagIds = searchOptions.tagIds;
                }

                if (!_.isNil(searchOptions.pageNumber)) {
                    queryParams.CurrentPage = searchOptions.pageNumber;
                }

                if (!_.isNil(searchOptions.pageSize)) {
                    queryParams.PageSize = searchOptions.pageSize;
                }

                if (!_.isNil(searchOptions.deletionTypes)) {
                    queryParams.DeletionTypes = searchOptions.deletionTypes;
                }

                if (!_.isNil(searchOptions.excludeLinkTypes)) {
                    queryParams.ExcludeLinkTypes = searchOptions.excludeLinkTypes;
                }
            }
            
            return $http.get(UrlService.getWebAPIUrl() + '/item' + dataSrv.convertQueryParams(queryParams)).then(
                function (response) {
                    return response.data;
                });
        }

        /**
         * Fetch details of an item.
         * (Note: Not necessarily the full details of the item, just the properties common to the master record type).
         *
         * @param {number} id - Id of the item to fetch.
         */
        function get(id) {
            return dataSrv.get(id);
        }

        /**
         * Permanently delete the items with the provided ids.
         *
         * @param {array} itemIds - Array of {number}
         * @return {promise} A promise that resolves if all items were permanently deleted, otherwise, rejects with a HTTP response.
         */
        function permanentlyDeleteItems(itemIds) {
            return $http.post(UrlService.getWebAPIUrl() + '/item/PermanentDeleteMultiple', itemIds);
        }

        /**
         * Delete the items with the provided ids.
         *
         * @param {array} itemIds - Array of {number}
         * @return {promise} A promise that resolves if all items were deleted, otherwise, rejects with a HTTP response.
         */
        function deleteItems(itemIds) {
            return $http.post(UrlService.getWebAPIUrl() + '/item/DeleteMultiple', itemIds);
        }

        /**
         * Copy the items with the provided ids.
         *
         * @param {array} itemIds - Array of {number}
         * @return {promise} A promise that resolves if there were no errors copying the items, otherwise, rejects with a HTTP response.
         *   The promise resolves a count {number} of the items that were copied.
         */
        function copyItems(itemIds) {
            return $http.post(UrlService.getWebAPIUrl() + '/item/CopyMultiple', itemIds)
                .then(function (response) {
                    return response.data;
                });
        }

        /**
         * Summarise, in plain text, the count of types for the provided list of items. E.g.
         * - 4 Notes
         * - 1 Sent Email
         * - 3 Tasks
         *
         * @param {array} items - An array of {object}, where each element has a property {string} Type, and
         *   may have additional item-specific properties like {string} EmailStatus.
         * @return {array} Array of strings, each element describing a count of item type, e.g. "4 Notes", "1 Sent Email", etc.
         */
        function summariseItemList(items) {
            var summary = [];

            // Create a copy of the list, because subsequent code removes items from the list to identify which have been processed.
            var itemsToSummarise = items.slice();

            _.forEach(itemTypeMetaData, function (itemType) {
                var itemTypeCount = _.remove(itemsToSummarise, itemType.predicate).length;

                if (itemTypeCount === 1) {
                    summary.push('1 ' + itemType.singular);
                } else if (itemTypeCount > 1) {
                    summary.push(itemTypeCount + ' ' + itemType.plural);
                }
            });

            // Items that don't have a type metadata match will just settle for a naive 's' suffix for plural descripton.
            if (itemsToSummarise.length > 0) {
                var groupedItems = _.groupBy(itemsToSummarise, 'Type');
                _.forEach(groupedItems, function (groupList, groupType) {
                    if (groupList.length === 1) {
                        summary.push('1 ' + groupType);
                    } else {
                        summary.push(groupList.length + ' ' + groupType + 's');
                    }
                });
            }

            return summary;
        }

        /**
         * Get item type metadata for the specified item.
         *
         * @return {object} Undefined if no meta data match could be found for the item.
         */
        function getItemTypeMetaData(item) {
            return _.find(itemTypeMetaData, function (type) { return _.isMatch(item, type.predicate); });
        }

        /**
         * Get a hyperlink for the provided item.
         *
         * @return {string} Hyperlink for the item, e.g. '#/email/42'. If the item type can't be determined,
         *   will return null.
         */
        function getItemLink(item) {
            if (!_.isNil(item) && !_.isNil(item.Type) && !_.isNil(item.Id)) {
                switch (item.Type) {
                    case 'Email':
                        return '/#/email/' + item.Id;
                    case 'Task':
                        return '/#/tasks/' + item.Id;
                    case 'SMS':
                        return '/#/sms/' + item.Id;
                    case 'DocumentTemplate':
                        return '/#/documenttemplates/' + item.Id;
                    case 'Entity':
                        return '/#/entities/' + item.Id;
                    case 'WorkflowInstance':
                        return '/#/runworkflow/' + item.Id;
                    case 'Note':
                        return '/#/notes/' + item.Id;
                    case 'File':
                        return '/#/files/' + item.Id;
                }
            }

            return null;
        }

        /**
         * @param {array} itemIds - Array of {number} for the items to be edited.
         * @param {number} relatedItemId - Id of the item (probably an entity) to assign all items to.
         * @return {promise}
         */
        function bulkEdit(itemIds, relatedItemId) {
            var requestBody = {
                ItemIds: itemIds,
                RelatedItemId: relatedItemId
            };

            return $http.post(UrlService.getWebAPIUrl() + '/item/EditMultiple', requestBody)
                .then(function (response) {
                    return response.data;
                });
        }

        /**
         * Restore the item with the provided id.
         *
         * @param {number} itemId - Id of item to restore
         * @return {promise} A promise that resolves if item was restored, otherwise, rejects with a HTTP response.
         */
        function restoreItem(itemId) {
            return $http.post(UrlService.getWebAPIUrl() + '/item/' + itemId + '/Restore');
        }

        /**
         * Restore the items with the provided ids.
         *
         * @param {array} itemIds - Ids of items to restore
         * @return {promise} A promise that resolves if the items are restored, otherwise, rejects with a HTTP response.
         */
        function restoreItems(itemIds) {
            return $http.post(UrlService.getWebAPIUrl() + '/item/Restore', itemIds);
        }

        /**
         * Gets the count of items
         *
         * @param {bool} deleted
         * @returns {promise} A promise that resolves with the count
         */
        function count(deleted) {
            var url = UrlService.getWebAPIUrl() + '/item/count';

            if (!_.isNil(deleted)) {
                url += '?deleted=' + deleted;
            }

            return $http.get(url).then(
                function (response) {
                    return response.data;
                });
        }

        /**
         * Get items usage for the provided ids.
         *
         * @param {array} itemIds - Array of {number}
         * @return {promise} A promise that resolves with a list of usages for the supplied item ids.
         */
        function getUsage(itemIds) {
            var url = UrlService.getWebAPIUrl() + '/item/Usage';
            var params = '';
            if (!_.isNil(itemIds)) {
                _.forEach(itemIds, function (itemId) {
                    if (params === '') {
                        params += '?';
                    } else {
                        params += "&";
                    }

                    params += 'ids=' + itemId;
                });
            }

            return $http.get(url + params)
                .then(function (response) {
                    return response.data;
                });
        }
    }
})();